import * as React from "react"
import { ArrowLeft, Check, Copy, FileText, Menu, X } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { ThemeToggle } from "@/components/ui/theme-toggle"
import { SidebarNav, type NavGroup } from "@/components/api-docs/SidebarNav"
import { cn } from "@/lib/utils"
import sectionsData from "@/components/api-docs/sections.json"

type Method = "GET" | "POST" | "PATCH" | "PUT" | "DELETE"

type Param = {
  name: string
  type: string
  required?: boolean
  description: string
}

type Endpoint = {
  method: Method
  path: string
  summary: string
  milestone: number
  description?: string
  params?: Param[]
  requestBody?: string
  responseShape?: string
  example?: string
  notes?: string
}

type Section = {
  id: string
  title: string
  description?: string
  endpoints: Endpoint[]
}

const METHOD_TONE: Record<Method, string> = {
  GET: "bg-accent-faded text-accent-display",
  POST: "bg-signal-faded text-signal-display",
  PATCH: "bg-signal-faded text-signal-display",
  PUT: "bg-signal-faded text-signal-display",
  DELETE: "border border-hairline bg-page text-ink-display",
}

function MethodBadge({ method }: { method: Method }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
        METHOD_TONE[method],
      )}
    >
      {method}
    </span>
  )
}

function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="overflow-x-auto rounded-md border border-hairline bg-surface px-3 py-2 text-xs leading-relaxed text-ink-display">
      <code>{children}</code>
    </pre>
  )
}

// Minimal inline-markdown renderer for the limited subset used in this page:
// paragraphs, `inline code`, **bold**, *italic*. Content is authored in this
// file, not user-supplied, but we still escape HTML defensively.
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
}

function renderInline(s: string): string {
  return escapeHtml(s)
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>")
}

function MarkdownText({ children, className }: { children: string; className?: string }) {
  const paragraphs = children.trim().split(/\n{2,}/)
  return (
    <div className={className}>
      {paragraphs.map((p, i) => (
        <p
          key={i}
          className={i === 0 ? "" : "mt-2"}
          dangerouslySetInnerHTML={{ __html: renderInline(p) }}
        />
      ))}
    </div>
  )
}

function EndpointCard({ endpoint }: { endpoint: Endpoint }) {
  return (
    <article className="rounded-lg border border-hairline bg-page p-4">
      <header className="flex flex-wrap items-center gap-2">
        <MethodBadge method={endpoint.method} />
        <code className="text-sm text-ink-display">{endpoint.path}</code>
        <Badge tone="muted" className="ml-auto">M{endpoint.milestone}</Badge>
      </header>
      <p className="mt-2 text-ink-body">{endpoint.summary}</p>
      {endpoint.description && (
        <MarkdownText className="mt-2 text-sm text-ink-muted">
          {endpoint.description}
        </MarkdownText>
      )}

      {endpoint.params && endpoint.params.length > 0 && (
        <div className="mt-4">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Parameters</h4>
          <ul className="mt-2 flex flex-col gap-1">
            {endpoint.params.map((p) => (
              <li key={p.name} className="text-sm">
                <code className="text-ink-display">{p.name}</code>
                <span className="ml-2 text-xs text-ink-muted">{p.type}</span>
                {p.required && (
                  <span className="ml-2 text-xs font-medium text-accent-display">required</span>
                )}
                <span className="ml-2 text-ink-muted">— {p.description}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {endpoint.requestBody && (
        <div className="mt-4">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Request body</h4>
          <div className="mt-2">
            <CodeBlock>{endpoint.requestBody}</CodeBlock>
          </div>
        </div>
      )}

      {endpoint.responseShape && (
        <div className="mt-4">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Response shape</h4>
          <div className="mt-2">
            <CodeBlock>{endpoint.responseShape}</CodeBlock>
          </div>
        </div>
      )}

      {endpoint.example && (
        <div className="mt-4">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Example (curl)</h4>
          <div className="mt-2">
            <CodeBlock>{endpoint.example}</CodeBlock>
          </div>
        </div>
      )}

      {endpoint.notes && (
        <MarkdownText className="mt-4 rounded-md border border-hairline bg-surface px-3 py-2 text-sm text-ink-muted">
          {endpoint.notes}
        </MarkdownText>
      )}
    </article>
  )
}

const SECTIONS: Section[] = sectionsData as unknown as Section[]

const NAV_GROUPS: NavGroup[] = [
  {
    label: "Getting started",
    items: [{ id: "authentication", label: "Authentication" }],
  },
  {
    label: "Endpoints",
    items: [
      { id: "clips", label: "Clips library" },
      { id: "agent-todos", label: "Agent todos" },
      { id: "agent-runs", label: "Agent runs" },
      { id: "events", label: "Events" },
    ],
  },
  {
    label: "Gaps",
    items: [{ id: "not-currently-exposed", label: "Not exposed via /api" }],
  },
]

function fenceFor(body: string): string {
  return body.includes("```") ? "~~~" : "```"
}

function endpointToMarkdown(endpoint: Endpoint): string {
  const out: string[] = []
  out.push(`### ${endpoint.method} ${endpoint.path}`)
  out.push("")
  out.push(`${endpoint.summary}  \nMilestone: M${endpoint.milestone}`)
  if (endpoint.description) {
    out.push("")
    out.push(endpoint.description)
  }
  if (endpoint.params && endpoint.params.length > 0) {
    out.push("")
    out.push("**Parameters**")
    out.push("")
    for (const p of endpoint.params) {
      const req = p.required ? " *(required)*" : ""
      out.push(`- \`${p.name}\` (${p.type})${req} — ${p.description}`)
    }
  }
  if (endpoint.requestBody) {
    const fence = fenceFor(endpoint.requestBody)
    out.push("")
    out.push("**Request body**")
    out.push("")
    out.push(`${fence}json`)
    out.push(endpoint.requestBody)
    out.push(fence)
  }
  if (endpoint.responseShape) {
    const fence = fenceFor(endpoint.responseShape)
    out.push("")
    out.push("**Response shape**")
    out.push("")
    out.push(`${fence}json`)
    out.push(endpoint.responseShape)
    out.push(fence)
  }
  if (endpoint.example) {
    const fence = fenceFor(endpoint.example)
    out.push("")
    out.push("**Example (curl)**")
    out.push("")
    out.push(`${fence}bash`)
    out.push(endpoint.example)
    out.push(fence)
  }
  if (endpoint.notes) {
    out.push("")
    out.push(`> ${endpoint.notes.replace(/\n/g, "\n> ")}`)
  }
  return out.join("\n")
}

function sectionToMarkdown(section: Section): string {
  const out: string[] = []
  out.push(`## ${section.title}`)
  if (section.description) {
    out.push("")
    out.push(section.description)
  }
  for (const endpoint of section.endpoints) {
    out.push("")
    out.push(endpointToMarkdown(endpoint))
  }
  return out.join("\n")
}

function docsToMarkdown(sections: Section[]): string {
  const out: string[] = []
  out.push("# Tubery API docs")
  out.push("")
  out.push("Live reference for every endpoint Tubery exposes to external agents.")
  for (const section of sections) {
    out.push("")
    out.push(sectionToMarkdown(section))
  }
  return out.join("\n") + "\n"
}

type CopyButtonProps = {
  getMarkdown: () => string
  label: string
  size?: "sm" | "icon"
  variant?: "ghost" | "secondary"
  className?: string
}

function CopyButton({ getMarkdown, label, size = "sm", variant = "ghost", className }: CopyButtonProps) {
  const [copied, setCopied] = React.useState(false)
  const timerRef = React.useRef<number | null>(null)

  React.useEffect(() => () => {
    if (timerRef.current) window.clearTimeout(timerRef.current)
  }, [])

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(getMarkdown())
      setCopied(true)
      if (timerRef.current) window.clearTimeout(timerRef.current)
      timerRef.current = window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard API failed (likely insecure context). Swallow silently —
      // the button will just not show feedback.
    }
  }

  const Icon = copied ? Check : Copy
  const text = copied ? "Copied" : label

  if (size === "icon") {
    return (
      <Button
        type="button"
        variant={variant}
        size="icon"
        onClick={onCopy}
        aria-label={text}
        className={className}
      >
        <Icon className="h-4 w-4" />
      </Button>
    )
  }

  return (
    <Button
      type="button"
      variant={variant}
      size="sm"
      onClick={onCopy}
      className={className}
    >
      <Icon className="h-4 w-4" />
      <span>{text}</span>
    </Button>
  )
}

export function ApiDocs() {
  const [navOpen, setNavOpen] = React.useState(false)

  return (
    <div className="min-h-screen bg-page text-ink-body">
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-hairline bg-page/80 px-4 backdrop-blur-sm sm:px-6">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setNavOpen(true)}
            className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-hairline bg-surface text-ink-body lg:hidden"
            aria-label="Open navigation"
          >
            <Menu className="h-4 w-4" />
          </button>
          <span className="font-display text-sm font-semibold text-ink-display">
            API docs
          </span>
        </div>
        <div className="flex items-center gap-2">
          <CopyButton
            getMarkdown={() => docsToMarkdown(SECTIONS)}
            label="Copy all as markdown"
          />
          <Button variant="ghost" size="sm" asChild>
            <a href="/api/docs.txt" aria-label="View plain-text version">
              <FileText className="h-4 w-4" />
              <span className="hidden md:inline">View as txt</span>
            </a>
          </Button>
          <Button variant="ghost" size="sm" asChild>
            <a href="/" aria-label="Back to home">
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden md:inline">Back to home</span>
            </a>
          </Button>
          <ThemeToggle />
        </div>
      </header>

      <div className="mx-auto flex max-w-7xl">
        <aside className="hidden w-64 shrink-0 border-r border-hairline lg:block">
          <div className="sticky top-14 max-h-[calc(100vh-3.5rem)] overflow-y-auto p-4">
            <SidebarNav groups={NAV_GROUPS} />
          </div>
        </aside>

        {navOpen && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <div
              className="absolute inset-0 bg-ink-display/40 backdrop-blur-sm"
              onClick={() => setNavOpen(false)}
            />
            <div className="absolute left-0 top-0 h-full w-72 overflow-y-auto bg-page p-4 shadow-xl">
              <div className="mb-4 flex items-center justify-between">
                <span className="font-display text-sm font-semibold text-ink-display">
                  API docs
                </span>
                <button
                  type="button"
                  onClick={() => setNavOpen(false)}
                  className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-ink-muted hover:text-ink-display"
                  aria-label="Close navigation"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <SidebarNav groups={NAV_GROUPS} onNavigate={() => setNavOpen(false)} />
            </div>
          </div>
        )}

        <main className="min-w-0 flex-1 px-4 py-8 sm:px-8 lg:px-12">
          <div className="mb-12 max-w-2xl">
            <h1>API docs</h1>
            <p className="mt-2 text-ink-muted">
              Live reference for every endpoint Tubery exposes to external agents.
              Each section is grouped by resource, with the milestone it shipped in.
            </p>
          </div>

          <div className="flex flex-col gap-12">
            {SECTIONS.map((section) => (
              <section key={section.id} id={section.id} className="flex scroll-mt-20 flex-col gap-3">
                <div>
                  <div className="flex items-center justify-between gap-3">
                    <h2>{section.title}</h2>
                    <CopyButton
                      getMarkdown={() => sectionToMarkdown(section)}
                      label="Copy section"
                    />
                  </div>
                  {section.description && (
                    <MarkdownText className="mt-1 max-w-2xl text-sm text-ink-muted">
                      {section.description}
                    </MarkdownText>
                  )}
                </div>
                {section.endpoints.length > 0 && (
                  <div className="flex flex-col gap-3">
                    {section.endpoints.map((endpoint) => (
                      <EndpointCard key={`${endpoint.method}-${endpoint.path}`} endpoint={endpoint} />
                    ))}
                  </div>
                )}
              </section>
            ))}
          </div>
        </main>
      </div>
    </div>
  )
}
